import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Alert,
  Box,
  Select,
  MenuItem,
  TextField,
  FormControl,
  Checkbox,
  Typography,
  InputLabel,
} from '@mui/material';
import FormHelperText from '@mui/material/FormHelperText';
import ListItemText from '@mui/material/ListItemText';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';

import axios from 'axios';

import { 
  AUDIENCE_UPDATE_ENDPOINT,
  AUDIENCE_DELETE_ENDPOINT,
  AUDIENCE_BULK_DELETE_ENDPOINT,
  SEGMENT_CREATE_ENDPOINT,
  SEGMENT_EDIT_ENDPOINT
} from '../../config/urls';

import { useAuth } from '../../hooks/AuthProvider';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

const AudienceFormDialog = ({ 
    modalState, 
    handleModalState, 
    selectedClient, 
    allSegments = [], 
    activeSegments = [], 
    allSegmentsDetails = [], 
    fetchData,
    selectedRecords, // New prop for selected records
    setSelectedRecords,
    audienceData // New prop for audience data
  }) => {
  
  const { getValidToken } = useAuth();
  
      // create segment variables
  const [createSegmentName, setCreateSegmentName] = useState('');
  const [createSegmentDescription, setCreateSegmentDescription] = useState('');

  // edit segment variables
  const [segmentCode, setSegmentCode] = useState('');
  const [selectedSegment, setSelectedSegment] = useState('Select a segment');
  
  // update segment variables
  const [selectedSegmentName, setSelectedSegmentName] = useState('');
  const [selectedSegmentDescription, setSelectedSegmentDescription] = useState('');
  const [tickedSegment, setTickedSegment] = useState(selectedClient?.segments ?? []);

  const [segmentToUpdate, setSegmentToUpdate] = useState('');

  const convertFiltersToQueryParams = (filters) => {
    if (!filters || filters.length === 0) return (new URLSearchParams({})).toString();

    let queryParams = new URLSearchParams({
      limit: 1,
      offset: 0,
    });

    filters.forEach(({ field, operator, value }) => {
      field = field.toLowerCase().replaceAll(" ", "_");
      operator = operator.toLowerCase().replaceAll(" ", "_");
      queryParams.append(field, `${operator}:${value ?? ""}`);
    });

    return queryParams.toString();
  };

  const handleNameChange = (event) => {
      const newName = event.target.value;
      
      if (newName === '' || newName.trim().length > 0) {
          if (newName.length <= 50) {
            setCreateSegmentName(newName);
          }
      }
  };

  const handleDescriptionChange = (event) => {
    const newDescription = event.target.value;
    
    if (newDescription === '' || newDescription.trim().length > 0) {
      if (newDescription.length <= 250) {
        setCreateSegmentDescription(newDescription);
      }
    }
  };

  const handleUpdatedNameChange = (event) => {
    const newName = event.target.value;
    
    if (newName === '' || newName.trim().length > 0) {
        if (newName.length <= 50) {
          setSelectedSegmentName(newName);
        }
    }
};

  const handleSegmentChange = (event) => {
    const selectedSegmentName = event.target.value;
    setSelectedSegment(selectedSegmentName);
    
    const selectedSegment = allSegmentsDetails.find(
      segment => segment.segment_name === selectedSegmentName
    );
    
    if (selectedSegment) {
      console.log("Selected Segment ", selectedSegment);
      setSelectedSegmentName(selectedSegment.segment_name);
      setSelectedSegmentDescription(selectedSegment.segment_description);
      setSegmentCode(selectedSegment.id);
    }
  };


  const [alertObj, setAlertObj] = useState({
    active: false,
    msg: "",
    type: ""
  });

  const handleAlertClose = () => {
    setAlertObj({
      active: false,
      type: "",
      msg: ""
    });
  };

  const handleClose = () => {
    handleModalState({ isOpen: false });
    handleAlertClose();
    
    setSegmentCode('');
    setSelectedSegment('Select a segment');
    setSelectedSegmentName('');
    setSelectedSegmentDescription('');
    setTickedSegment([]);
    setCreateSegmentName('');
    setCreateSegmentDescription('');

    fetchData();
  };

  const handleActionUpdateSave = async() => {

    if (!tickedSegment || tickedSegment.length === 0) {
      setAlertObj({
        active: true,
        type: "error",
        msg: "Please select at least one segment"
      })
      return;
    }

    const apiUrl =  AUDIENCE_UPDATE_ENDPOINT;

    console.log("API URL: ", apiUrl);

    // Get the IDs of all selected records
    const selectedAudiences = audienceData.filter(audience => 
      selectedRecords.includes(audience.id)
    );

    // Create an array of objects with phone numbers and the segment to update
    const updatePayload = selectedAudiences.map(audience => ({
      phone_no: audience.phone_no,
      segment_list: tickedSegment // Add the selected segment
    }));

    console.log("Payload: ", updatePayload);

    const token = await getValidToken();
    const tenant = localStorage.getItem('tenant');

    // We'll do a batch update for all selected records
    try {
      const promises = updatePayload.map(data => 
        axios.post(apiUrl, data, {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant
          },
        })
      );
      
      await Promise.all(promises);
      
      setAlertObj({
        active: true,
        type: "success",
        msg: "Segment Updated for selected users only"
      });
      
      // Close after a short delay and reset segment
      setTimeout(() => {
        setSegmentToUpdate('');
        handleClose();
      }, 1500);
    } catch (error) {
      console.error('API Error:', error);
      setAlertObj({
        active: true,
        type: "error",
        msg: "Failed to Update Customer Segment"
      });
    }
  };

  const handleDeleteAudienceButton = async() => {

    const apiUrl =  AUDIENCE_DELETE_ENDPOINT;

    console.log("Delete Audience API URL: ", `${apiUrl}${modalState.selectedClient.id}/`);

    const token = await getValidToken();
    const tenant = localStorage.getItem('tenant');

    axios.delete(`${apiUrl}${modalState.selectedClient.id}/` , {
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant
      },
    })
      .then(response => {
        console.log('API Response:', response.data);
        setAlertObj({
          active: true,
          type: "success",
          msg: "Audience Deleted Successfully"
        });
        // handleClose();
      })
      .catch(error => {
        console.error('API Error:', error.response.data);
        setAlertObj({
          active: true,
          type: "error",
          msg: "Failed to Delete Audience"
        });
      })
      .finally(() => {
        setTimeout(() => {
          handleClose()
        }, 2000);
      });
  };

  const handleDeleteSelectedAudiencesButton = async () => {
    const apiUrl = AUDIENCE_BULK_DELETE_ENDPOINT; 

    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    try {
      const response = await axios.delete(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
        data: {
          ids: modalState?.selectedClients, 
        },
      });

      setAlertObj({
        active: true,
        type: "success",
        msg: "Selected Audiences Deleted Successfully",
      });

      setSelectedRecords([]);      
    } catch (error) {
      console.error("API Error:", error.response?.data || error.message);
      setAlertObj({
        active: true,
        type: "error",
        msg: "Failed to Delete Selected Audiences",
      });
    } finally {
      setTimeout(() => {
        handleModalState(prev => {
          return { ...prev, selectedClients: [] }
        });
        handleClose();
      }, 2000);
    }
  };

  const handleDeleteAllAudiencesButton = async () => {
    const apiUrl = AUDIENCE_BULK_DELETE_ENDPOINT; 

    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    try {
      const response = await axios.delete(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
        data: {
          ids: [], 
          all: true,
          active_filters: convertFiltersToQueryParams(modalState?.activeFilters || []) || "",
        },
      });

      setAlertObj({
        active: true,
        type: "success",
        msg: "Filtered Audiences Deleted Successfully",
      });      
    } catch (error) {
      console.error("API Error:", error.response?.data || error.message);
      setAlertObj({
        active: true,
        type: "error",
        msg: "Failed to Delete Filtered Audiences",
      });
    } finally {
      setTimeout(() => {
        handleModalState(prev => {
          return { ...prev, activeFilters: [], count: 0 }
        });
        handleClose();
      }, 2000);
    }
  };

  const handleEditSegmentButton = async() => {

    const apiUrl =  SEGMENT_EDIT_ENDPOINT;
    console.log("API URL: ", apiUrl);

    if (selectedSegment === 'Select a segment') {
      setAlertObj({
        active: true,
        type: "error",
        msg: "Please select a segment"
      });
      return;
    }

    const data = {
      "segment_code": segmentCode,
      "segment_name": selectedSegmentName,
      "segment_description": selectedSegmentDescription
    };
    console.log("Payload: ", data);

    const token = await getValidToken();
    const tenant = localStorage.getItem('tenant');

    axios.post(apiUrl, data, {
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant
        
      },
    })
      .then(response => {
        console.log('Edit API Response:', response.data);
        setAlertObj({
          active: true,
          type: "success",
          msg: "Segment Updated Successfully"
        });
        // handleClose();
      })
      .catch(error => {
        console.error('API Error:', error.response.data);
        setAlertObj({
          active: true,
          type: "error",
          msg: "Failed to Update Segment"
        });
      });
  };

  const handleInactiveButton = async() => {

    const apiUrl =  SEGMENT_EDIT_ENDPOINT;
    console.log("API URL: ", apiUrl);

    if (selectedSegment === 'Select a segment') {
      setAlertObj({
        active: true,
        type: "error",
        msg: "Please select a segment"
      });
      return;
    }

    const data = {
      "segment_code": segmentCode,
      "segment_name": selectedSegment,
      "active": activeSegments.includes(selectedSegment) ? false : true
    };
    console.log("Payload: ", data);

    const token = await getValidToken();
    const tenant = localStorage.getItem('tenant');

    axios.post(apiUrl, data, {
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant
      }
    })
      .then(response => {
        console.log('Segment Status API Response:', response.data);
        setAlertObj({
          active: true,
          type: "success",
          msg: `${response.data.message}`
        });
      })
      .catch(error => {
        console.error('API Error:', error.response.data);
        setAlertObj({
          active: true,
          type: "error",
          msg: "Failed to Update Segment"
        });
      });
  };

  const handleCreateSegmentSave = async() => {

    const apiUrl = SEGMENT_CREATE_ENDPOINT;

    console.log("API URL: ", apiUrl);

    if (createSegmentName === '') {
      setAlertObj({
        active: true,
        type: "error",
        msg: "Please enter a name for the segment"
      });
      return;
    }


    const data = {
      "segment_name": createSegmentName,
      "segment_description": createSegmentDescription,
    };

    console.log("Payload: ", data);

    const token = await getValidToken();
    const tenant = localStorage.getItem('tenant');

    axios.post(apiUrl, data, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        "X-Tenant-Id": tenant
      },
    })
      .then(response => {
        console.log('Create Segment API Response:', response.data);
        setAlertObj({
          active: true,
          type: "success",
          msg: "Segment Created Successfully"
        });
        // handleClose();
      })
      .catch(error => {
        console.error('API Error:', error.response.data);
        setAlertObj({
          active: true,
          type: "error",
          msg: error.response.data.message
        });
      });
  };

  const createSegmentBody = () => {
    return (
      <>
        <div style={{ position: 'relative' }}>
          <strong>Name: *</strong>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            placeholder="Enter name"
            type="text"
            fullWidth
            onChange={handleNameChange}
            value={createSegmentName}
            required
          />
          <Typography 
              variant="body2" 
              sx={{ 
                color: 'gray', 
                position: 'absolute', 
                right: '10px', 
                top: '10px',
                pointerEvents: 'none'
              }}>
              {`${createSegmentName.length}/50`}
          </Typography>
        </div>

        <div style={{ position: 'relative' }}>
          <strong>Description:</strong>
          <TextField
            autoFocus
            margin="dense"
            id="description"
            placeholder="Enter description"
            multiline
            rows={4}
            type="text"
            fullWidth
            // onChange={(e) => setCreateSegmentDescription(e.target.value)}
            onChange={handleDescriptionChange}
            value={createSegmentDescription}
          />
          <Typography 
            variant="body2"
            sx={{ 
              color: 'gray',
              position: 'absolute',
              right: '10px',
              top: '10px',
              pointerEvents: 'none'
            }}>
            {`${createSegmentDescription.length}/250`}
          </Typography>
        </div>
      </>
    );
  };

  const editSegmentBody = () => {
    return (
      <>
        <div style={{ position: 'relative' }}>
          <strong>Select a Segment:</strong>
          <Select 
            value={selectedSegment} 
            onChange={handleSegmentChange}
            fullWidth
            sx={{ mt: 1 }}
          >
            <MenuItem value="Select a segment">
              Select a segment
            </MenuItem>
            {allSegments.map((segment, index) => (
              <MenuItem 
                key={index} 
                value={segment}
              >
                {segment}
              </MenuItem>
            ))}
          </Select>
        </div>

        <div style={{ position: 'relative', marginTop: '20px' }}>
          <strong>Name:</strong>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            placeholder="Enter name"
            type="text"
            fullWidth
            value={selectedSegmentName}
            onChange={handleUpdatedNameChange}
          />
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'gray', 
              position: 'absolute', 
              right: '10px', 
              top: '10px',
              pointerEvents: 'none'
            }}>
            {`${selectedSegmentName.length}/50`}
          </Typography>
        </div>

      </>
    );
  };

  const updateCustomerSegmentBody = () => {
    console.log("tickedSegment: ", tickedSegment);
    return (
      <Box sx={{ textAlign: 'left', mb: 0, mx: 2}}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Select one or more segments to add customers to an existing segment
        </Typography>
        
        {/* Multi-select dropdown with checkboxes */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="multiple-segment-label">Select Segments *</InputLabel>
          <Select
            label="Select Segments *"
            labelId="multiple-segment-label"
            multiple
            value={tickedSegment}
            onChange={(event) => {
              setTickedSegment(event.target.value);
            }}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return <em>Please select at least one segment</em>;
              }
              return selected.join(', ');
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300
                },
              },
            }}
          >
            {activeSegments.length === 0 ? (
              <MenuItem disabled>No segments found!</MenuItem>
            ) : (
              activeSegments.map((segment, index) => (
                <MenuItem key={index} value={segment}>
                  <Checkbox checked={tickedSegment.includes(segment)} />
                  <ListItemText primary={segment} />
                </MenuItem>
              ))
            )}
          </Select>
          {tickedSegment.length === 0 && (
            <FormHelperText>Please select at least one segment</FormHelperText>
          )}
        </FormControl>
        
        {/* Display selection count */}
        {selectedRecords && selectedRecords.length > 0 && (
          <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2">
              Selected Records: <strong>{selectedRecords.length}</strong>
            </Typography>
            {tickedSegment.length > 0 && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Selected Segments: <strong>{tickedSegment.join(', ')}</strong>
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const renderDialogActions = () => {
    if (modalState.mode === 'create_segment') {
      return (
        <>
          <Button variant="contained" onClick={handleCreateSegmentSave}>Save</Button>
          <Button variant="contained" onClick={handleClose}>Close</Button>
        </>
      );
    } else if (modalState.mode === 'edit_segment') {
      return (
        <>
          <Button variant="contained" onClick={handleEditSegmentButton}>Save</Button>
          <Button variant="contained" onClick={handleInactiveButton}>
            {activeSegments.includes(selectedSegment) ? "Inactive" : "Active"}
          </Button>
          <Button variant="contained" onClick={handleClose}>Close</Button>
        </>
      );
    } else if (modalState.mode === 'update_customer_segments') {
      return (
        <>
          <Button 
            variant="contained" 
            disabled={tickedSegment.length === 0} 
            onClick={handleActionUpdateSave}
          >
            Save
          </Button>
          <Button variant="contained" onClick={handleClose}>Close</Button>
        </>
      );
    } else if (modalState.mode === 'delete_audience') {
      return (
        <>
          <Button variant="contained" color="error" onClick={handleDeleteAudienceButton}>Yes</Button>
          <Button variant="contained" onClick={handleClose}>No</Button>
        </>
      );
    } else if (modalState.mode === 'delete_selected_audience') {
      return (
        <>
          <Button variant="contained" color="error" onClick={handleDeleteSelectedAudiencesButton}>Yes</Button>
          <Button variant="contained" onClick={handleClose}>No</Button>
        </>
      );
    } else if (modalState.mode === 'delete_all_audience') {
      return (
        <>
          <Button variant="contained" color="error" onClick={handleDeleteAllAudiencesButton}>Yes</Button>
          <Button variant="contained" onClick={handleClose}>No</Button>
        </>
      );
    } else {
      return (
        <Button autoFocus onClick={handleClose}>Close</Button>
      );
    }
  };

  const deleteAudienceBody = () => {
    console.log("modalState.selectedClient: ", modalState.selectedClient);

    return (
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <p>Are you sure you want to delete the Audience: <strong>{modalState.selectedClient.name}</strong></p>
      </Box>
    );
  };

  const deleteSelectedAudienceBody = () => {
    console.log("modalState.selectedClients: ", modalState?.selectedClients);

    return (
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <p>Are you sure you want to delete <strong>{modalState?.selectedClients?.length}</strong> Audiences</p>
      </Box>
    );
  };

  const deleteAllAudienceBody = () => {
    return (
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <p>Are you sure you want to delete <strong>{modalState?.count}</strong> Audiences</p>
      </Box>
    );
  };

  useEffect(() => {
    // Pre-select segments from selectedClient when modal opens
    setTickedSegment(selectedClient?.segments ?? []);
  }, [modalState.isOpen, selectedClient]);

  useEffect(() => {
    if (selectedSegment === 'Select a segment') {
      setSelectedSegmentName('');
      setSelectedSegmentDescription('');
    }
  }, [segmentCode, selectedSegment, selectedSegmentName, selectedSegmentDescription]);


  useEffect(() => {
      let timeoutId;
      
      if (alertObj.active) {
      timeoutId = setTimeout(() => {
          handleAlertClose();
      }, 5000);
      }
      
      // Cleanup function to clear the timeout when component unmounts or when alertObj changes
      return () => {
          if (timeoutId) {
              clearTimeout(timeoutId);
          }
      };
  }, [alertObj]);

  useEffect(() => {
    // When modal opens with selected records, pre-populate their segments
    if (modalState.isOpen && modalState.mode === 'update_customer_segments' && selectedRecords && selectedRecords.length > 0) {
      // Get all segments of selected records
      const selectedAudiences = audienceData.filter(audience => 
        selectedRecords.includes(audience.id)
      );
      
      // Extract segments from all selected records
      const segments = new Set();
      selectedAudiences.forEach(audience => {
        if (audience.segments && audience.segments.length > 0) {
          audience.segments.forEach(segment => segments.add(segment));
        }
      });
      
      // Set the pre-populated segments
      setTickedSegment(Array.from(segments));
    }
  }, [modalState.isOpen, selectedRecords, audienceData, modalState.mode]);


  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={modalState.isOpen}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ m: 0, p: 2, position: 'relative' }} id="customized-dialog-title">
        {/* Conditionally render title based on modalState.mode */}
        {modalState.mode === 'edit_segment' ? "Edit Segment"
          : modalState.mode === 'create_segment' ? "Create Segment"
            : modalState.mode === 'update_customer_segments' ? "Edit segment"
              : modalState.mode === 'delete_audience' ? "Delete Audience"
                : modalState.mode === 'delete_selected_audience' ? "Delete Selected Audience"
                  : modalState.mode === 'delete_all_audience' ? "Delete All Audiences"
                    : ''}

        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 0,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {alertObj.active && (
          <Alert sx={{ mb: 2 }} severity={alertObj.type} onClose={handleAlertClose}>
            {alertObj.msg}
          </Alert>
        )}

        {
        modalState.mode === 'create_segment' ? (
          createSegmentBody()
        ) :
          modalState.mode === 'edit_segment' ? (
            editSegmentBody()
          ) :
            (modalState.mode === 'update_customer_segments') ? (
              updateCustomerSegmentBody()
            ) :
              (modalState.mode === 'delete_audience') ? (
                deleteAudienceBody()
              ) : (modalState.mode === 'delete_selected_audience') ? (
                deleteSelectedAudienceBody()
                ) : (modalState.mode === 'delete_all_audience') ? (
                  deleteAllAudienceBody()
                ) : null
        }
      </DialogContent>
      
      <DialogActions>
        {renderDialogActions()}
      </DialogActions>
    </BootstrapDialog>
  );
}

export default AudienceFormDialog;  
